import AppMenu from "@/components/ui/menu";
import AppInput from "@/components/ui/input/AppInput";
import { isObject } from "@utils/helpers";
import AppSelectChevron from "./AppSelectChevron.vue";
import AppSelectDropdown from "./AppSelectDropdown.vue";

export default {
  name: "AppSelect",

  components: {
    AppMenu,
    AppInput,
    AppSelectChevron,
    AppSelectDropdown
  },

  model: {
    prop: "value",
    event: "change"
  },

  props: {
    readonly: Boolean,
    options: {
      type: Array,
      required: true
    },
    labelKey: {
      type: String,
      default: "title"
    },
    valueKey: {
      type: String,
      default: "value"
    },
    placeholder: {
      type: String,
      default: ""
    },
    multiple: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: "first"
    },
    size: {
      type: String,
      default: ""
    },
    // handle incorrect values
    incorrect: {
      type: Boolean,
      default: true
    },
    maxListHeight: {
      type: [String, Number],
      default: 190
    },
    loading: Boolean,
    remoteSearch: Function,
    remoteSearchText: {
      type: String,
      default: "Введите значение"
    },
    invalid: Boolean,
    value: {}
  },

  data() {
    return {
      opened: false,
      searchValue: ""
    };
  },

  computed: {
    isActive() {
      return this.opened;
    },
    classes() {
      return {
        [`app-select_theme_${this.theme}`]: !!this.theme,
        "app-select_hidden-input": this.hasSlot,
        "is-opened": this.opened
      };
    },
    hasSlot() {
      return !!this.$scopedSlots.default && (this.value || !this.placeholder);
    },
    transformedOptions() {
      return this.options.map(option =>
        isObject(option) ? option : this.transformOption(option)
      );
    },
    filteredOptions() {
      return this.transformedOptions.filter(this.checkOptionFilter);
    },
    valueTitle() {
      return this.getValueTitle(this.value);
    },
    finalOptions() {
      return this.remoteSearch ? this.transformedOptions : this.filteredOptions;
    },
    multipleText() {
      if (!this.multiple) return "";
      return this.value.join(", ");
    },
    selectionText() {
      const { multiple, valueTitle } = this;
      return multiple ? this.multipleText : valueTitle || "";
    },
    selectedOption() {
      return this.transformedOptions.find(
        item => item[this.valueKey] === this.value
      );
    },
    hasValue() {
      return this.multiple
        ? Array.isArray(this.value) && this.value.length > 0
        : this.value !== undefined && this.value !== null && this.value !== "";
    },
    needToShowClear() {
      return this.clearable && !this.disabled && this.hasValue;
    },
    isReadonly() {
      return (!this.remoteSearch && !this.searchable) || this.disabled;
    },
    isSearchActive() {
      return (this.remoteSearch || this.searchable) && this.isActive;
    },
    compPlaceholder() {
      const { isSearchActive, valueTitle, placeholder } = this;
      return isSearchActive && valueTitle ? valueTitle : placeholder;
    },
    resultValue() {
      return this.isSearchActive ? this.searchValue : this.selectionText;
    },
    normalizedSearchValue() {
      return this.searchValue.toLowerCase();
    }
  },

  methods: {
    getActivator() {
      return this.$el;
    },
    closeCondition(target) {
      return this.opened && !this.$el.contains(target);
    },
    checkOptionFilter(option) {
      const label = option[this.labelKey];
      const nameCheck = label
        .toLowerCase()
        .includes(this.normalizedSearchValue);
      return nameCheck;
    },
    transformOption(option) {
      return {
        [this.labelKey]: option,
        [this.valueKey]: option
      };
    },
    checkSelection(option) {
      const val = option[this.valueKey];
      if (this.multiple) {
        return this.value.includes(val);
      } else {
        return this.value === val;
      }
    },
    selectOption(option) {
      if (this.multiple) {
        this.$emit("change", this.value.concat(option[this.valueKey]));
      } else {
        this.$emit("change", option[this.valueKey]);
      }
    },
    unselectOption(optionValue) {
      if (this.multiple) {
        const filteredArray = this.value.filter(item => item !== optionValue);
        this.$emit("change", filteredArray);
      } else if (this.allowEmpty) {
        this.$emit("change", undefined);
      }
    },
    getValueTitle(value) {
      if (value == null) return "";
      const item = this.transformedOptions.find(
        item => item[this.valueKey] === value
      );
      if (!item && !this.incorrect) {
        throw new Error("Selected value has not been found");
      }
      return (item && item[this.labelKey]) || value;
    },
    handleSelect(option) {
      if (this.checkSelection(option)) {
        this.unselectOption(option[this.valueKey]);
      } else {
        this.selectOption(option);
      }
      if (!this.multiple) this.$refs.input.blur();
    },
    open() {
      this.opened = true;
      this.setSearchValue();
    },
    close() {
      this.opened = false;
    },
    handleFocus() {
      this.open();
    },
    handleBlur() {
      this.close();
    },
    handleClick() {
      if (this.disabled) return;
      if (this.opened) {
        this.$refs.input.blur();
      } else {
        this.$refs.input.focus();
      }
    },
    scrollToSelected() {
      if (!this.opened) return;
      this.$refs.dropdown.scrollToSelected();
    },
    onEscape() {
      this.$refs.input.blur();
    },
    clear() {
      const value = this.multiple ? [] : undefined;
      this.$emit("change", value);
    },
    handleInput(value = "") {
      this.setSearchValue(value);
      if (this.remoteSearch) {
        this.remoteSearch(value);
      }
    },
    setSearchValue(value = "") {
      this.searchValue = value;
    }
  }
};
